import config from "./siteRoutes.json";
import { normalizeSiteLanguage, SiteLanguage } from "./language";

export type SiteLocale = keyof typeof config.locales;
export const SITE_ORIGIN = config.origin;
export const DEFAULT_LOCALE = config.defaultLocale as SiteLocale;
export const SITE_LOCALES = Object.keys(config.locales) as SiteLocale[];
export const LOCALE_INFO = config.locales;

export const getPathLocale = (pathname: string): SiteLocale | null => {
  const prefix = pathname.split("/")[1];
  return SITE_LOCALES.includes(prefix as SiteLocale) ? prefix as SiteLocale : null;
};

export const stripPathLocale = (pathname: string): string =>
  getPathLocale(pathname) ? pathname.replace(/^\/[^/]+/, "") || "/" : pathname;

export const localeForLanguage = (language?: string | null): SiteLocale => {
  const normalized = normalizeSiteLanguage(language);
  return SITE_LOCALES.find(locale => LOCALE_INFO[locale].language === normalized) || DEFAULT_LOCALE;
};

export const languageForLocale = (locale: SiteLocale): SiteLanguage =>
  LOCALE_INFO[locale].language as SiteLanguage;

export const isPublicContentPath = (pathname: string): boolean =>
  config.publicPaths.includes(pathname) || /^\/news\/[^/]+$/.test(pathname);

export const pathForLocale = (pathname: string, locale: SiteLocale): string => {
  const base = stripPathLocale(pathname);
  return `/${locale}${base === "/" ? "" : base}`;
};

// Localize content URLs only. Assets, payment/account routes, external links,
// mailto/tel links, queries and fragments retain their existing behavior.
export const localizeHref = (href: string, locale: SiteLocale): string => {
  if (!href || href.startsWith("#")) return href;
  let relative = href;
  const absolute = /^https?:\/\//i.test(href);
  if (absolute) {
    const url = new URL(href);
    if (url.origin !== SITE_ORIGIN) return href;
    relative = url.pathname + url.search + url.hash;
  }
  if (!relative.startsWith("/") || relative.startsWith("//")) return href;
  const match = relative.match(/^([^?#]*)(.*)$/);
  if (!match) return href;
  const base = stripPathLocale(match[1]);
  if (!isPublicContentPath(base)) return href;
  const result = pathForLocale(base, locale) + match[2];
  return absolute ? SITE_ORIGIN + result : result;
};

// Content is repository-owned HTML. Rewrite only anchor href attributes;
// image/media URLs and external references must remain unchanged.
export const localizeHtmlLinks = (html: string, locale: SiteLocale): string =>
  html.replace(/(<a\b[^>]*\bhref\s*=\s*)(["'])(.*?)\2/gi,
    (_, start: string, quote: string, href: string) =>
      start + quote + localizeHref(href, locale) + quote);
